import { useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Pencil, Trash2, Check, X, Radio } from "lucide-react";
import { NfcDialog } from "@/components/NfcDialog";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLocations } from "@/components/LocationSelect";
import { LocationGridForm } from "@/components/LocationGridForm";
import { CellarMap } from "@/components/CellarMap";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export default function Locations() {
  const { user } = useAuth();
  const isOwner = user.role === "owner";
  const { locations, reload } = useLocations();
  const [nfcLoc, setNfcLoc] = useState(null);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/locations", { name, description: desc });
      setName("");
      setDesc("");
      reload();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
      <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-3">Cellar map</p>
      <h2 className="font-serif text-4xl sm:text-5xl text-[#F5F0EB] mb-8">Locations</h2>
      {isOwner && (
        <form onSubmit={add} className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 mb-8">
          <Input required value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Rack C - Shelf 4" className="h-11 bg-[#161214]" data-testid="location-name-input" />
          <Input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Description (optional)" className="h-11 bg-[#161214]" data-testid="location-description-input" />
          <Button type="submit" className="h-11 bg-[#5C1D24] hover:bg-[#8C2B36] text-[#F5F0EB]" data-testid="add-location-button">Add location</Button>
        </form>
      )}
      {isOwner && (
        <details className="rounded-xl border border-[#2E252A] bg-[#161214] p-5 mb-8" data-testid="location-grid-panel">
          <summary className="cursor-pointer text-sm text-[#D4AF37]" data-testid="location-grid-toggle">Create many at once — columns × shelves</summary>
          <div className="mt-5 max-w-lg"><LocationGridForm onDone={reload} /></div>
        </details>
      )}
      <CellarMap locations={locations} />
      <div className="rounded-xl border border-[#2E252A] divide-y divide-[#2E252A]" data-testid="locations-list">
        {locations.map((l) => <LocationRow key={l.id} loc={l} isOwner={isOwner} reload={reload} onNfc={setNfcLoc} />)}
        {!locations.length && <p className="p-8 text-center text-[#6E665D]">No locations yet.</p>}
      </div>
      <NfcDialog loc={nfcLoc} onOpenChange={(v) => !v && setNfcLoc(null)} />
    </div>
  );
}

const LocationRow = ({ loc, isOwner, reload, onNfc }) => {
  const [edit, setEdit] = useState(false);
  const [name, setName] = useState(loc.name);
  const [desc, setDesc] = useState(loc.description || "");
  const run = async (fn) => {
    try {
      await fn();
      setEdit(false);
      reload();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  if (edit) {
    return (
      <div className="flex gap-2 p-3">
        <Input value={name} onChange={(e) => setName(e.target.value)} data-testid={`location-edit-name-${loc.id}`} />
        <Input value={desc} onChange={(e) => setDesc(e.target.value)} data-testid={`location-edit-desc-${loc.id}`} />
        <Button size="icon" variant="ghost" onClick={() => run(() => api.put(`/locations/${loc.id}`, { name, description: desc }))} data-testid={`location-save-${loc.id}`}><Check className="w-4 h-4" /></Button>
        <Button size="icon" variant="ghost" onClick={() => setEdit(false)}><X className="w-4 h-4" /></Button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-4 px-5 py-4 hover:bg-[#161214] transition-colors" data-testid={`location-row-${loc.id}`}>
      <Link to={`/locations/${loc.id}`} className="flex-1 group" data-testid={`location-open-${loc.id}`}>
        <p className="text-[#F5F0EB] group-hover:text-[#D4AF37] transition-colors">{loc.name}</p>
        {loc.description && <p className="text-xs text-[#6E665D]">{loc.description}</p>}
        {loc.last_inventory && (
          <p className={`text-[11px] font-mono mt-0.5 ${loc.last_inventory.missing ? "text-[#E5A93C]" : "text-[#6E665D]"}`} data-testid={`location-last-inventory-${loc.id}`}>
            Checked {fmtDateTime(loc.last_inventory.at)}{loc.last_inventory.missing ? ` · ${loc.last_inventory.missing} missing` : ""}
          </p>
        )}
      </Link>
      <span className="font-mono text-xs text-[#D4AF37]">{loc.bottle_count} btl</span>
      <button onClick={() => onNfc(loc)} className="text-[#A89F91] hover:text-[#D4AF37]" data-testid={`location-nfc-${loc.id}`} aria-label="NFC tag"><Radio className="w-4 h-4" /></button>
      {isOwner && (
        <>
          <button onClick={() => setEdit(true)} className="text-[#A89F91] hover:text-[#D4AF37]" data-testid={`location-edit-${loc.id}`} aria-label="Edit"><Pencil className="w-4 h-4" /></button>
          <button onClick={() => window.confirm(`Delete ${loc.name}?`) && run(() => api.delete(`/locations/${loc.id}`))} className="text-[#A89F91] hover:text-[#8C2B36]" data-testid={`location-delete-${loc.id}`} aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
        </>
      )}
    </div>
  );
};
